import { every, isArray, isBoolean, isDate, isEmpty, isFunction, isObjectLike, isRegExp, merge } from 'lodash';

const PROPERTIES = 'properties';
const OPENED = 'isOpen';

/* eslint-disable no-underscore-dangle */

/**
 * Pre-defined set of callbacks, matching a particular action.
 * @typedef {Object} ModalInstanceCallbackSet
 * @property {function} [onChange] - executing when _opened|_props has changed.
 * @property {function} [onClose] - executing before calling 'close' (must return true to actually close the modal).
 */

/**
 * Undefined set of properties to be fed in corresponding {@link ModalInstance}.
 *
 * @typedef {Object} ModalInstancePropertySet
 */

/**
 * Instance modeling the outsourced state of the modal component.
 *
 * @returns {Object} an obfuscated object wrapping ModalInstance methods
 * @throws {Error} when argument passed to constructor is not a map of functions
 * @since 1.6.0 (probably below)
 */
function ModalInstance() {
    // --- Private Variables --- //

    /**
     * Register of {@link ModalInstanceCallbackSet} to be iterated over when executing a callback.
     * @type {Array<ModalInstanceCallbackSet>}
     */
    let _callbacks = [];

    /**
     * Maintained 'opening' state of corresponding {@link ModalInstance}.
     * @type {boolean}
     */
    let _opened = false;

    /**
     * Register of {@link ModalInstancePropertySet} to be maintained for corresponding {@link ModalInstance}.
     * @type {ModalInstancePropertySet}
     */
    let _props = {};

    // --- Private Methods --- //

    /**
     * @memberOf ModalInstance
     * @private
     */
    function _updateWith() {
        const value = merge({}, { [OPENED]: _opened, [PROPERTIES]: _props });
        _callbacks.forEach((callback) => (isFunction(callback.onChange) ? callback.onChange(value) : null));
    }

    /**
     * @memberOf ModalInstance
     * @private
     */
    function _onClose(params) {
        return _callbacks.every((callback) => {
            if (isFunction(callback.onClose)) {
                return callback.onClose(params);
            }
            return true;
        });
    }

    /**
     * @memberOf ModalInstance
     * @private
     * @param {ModalInstanceCallbackSet} callbacks set of callbacks to be registered
     * @throws {TypeError} when argument "callbacks" is not a map of functions
     */
    function _pushCallbacks(callbacks) {
        const prefix = 'ModalInstance#addCallbacks:';

        if (!isObjectLike(callbacks) || isEmpty(callbacks)) {
            throw new TypeError(`${prefix} argument "callbacks" should be a non-empty 'Object' (${typeof callbacks})`);
        }

        const isOnlyFunctions = every(callbacks, isFunction);

        // every property of 'callbacks' should be a function
        if (!isOnlyFunctions) {
            throw new TypeError(`${prefix} argument "callbacks" should only contain 'Function' (${isOnlyFunctions})`);
        }

        const duplicate = _callbacks.filter((clbs) => {
            return clbs === callbacks;
        });

        const duplicateOnClose = _callbacks.filter((clbs) => {
            return clbs.onClose === callbacks.onClose;
        });

        // If callback is not a duplicate, add it to the list
        // Else ignore it
        if (duplicate.length === 0 && duplicateOnClose.length === 0) {
            _callbacks.push(callbacks);
        }
    }

    // --- Public Methods --- //

    /**
     * Update registered props by merging previous ones with given ones
     *
     * @memberOf ModalInstance
     * @public
     * @param {ModalInstancePropertySet} props - plain object
     * @throws {TypeError} when is array|regex|date or not a plain object
     */
    function updateProps(props) {
        if (!isObjectLike(props) || isArray(props) || isRegExp(props) || isDate(props)) {
            throw new TypeError(`ModalInstance#updateProps - [props] is not a plain object, (${typeof props})`, props);
        }

        _props = merge({}, _props, props);
        _updateWith();
    }

    /**
     * Get the registered props for this modalInstance.
     * It always returns a different object reference to prevent modifying internal state.
     *
     * @memberOf ModalInstance
     * @public
     * @return {ModalInstancePropertySet} registered props for this modalInstance
     */
    function peekAtProps() {
        return { ..._props };
    }

    /**
     * Set state for the modal to be close.
     *
     * @memberOf ModalInstance
     * @public
     * @param {boolean} skip a boolean allowing to forcefully ignore closing the modal.
     */
    function close(skip, params) {
        if ((isBoolean(skip) && skip) || _onClose(params)) {
            _opened = false;
            _updateWith();
        }
    }

    /**
     * Set state for the modal to be open
     *
     * @memberOf ModalInstance
     * @public
     */
    function open(params) {
        _opened = true;
        updateProps({ ...params });
    }

    /**
     * Returns whether the modal is opened of not.
     *
     * @memberOf ModalInstance
     * @public
     * @return {boolean} whether the modal is opened of not
     */
    function isOpen() {
        return _opened;
    }

    /**
     * Returns an array of registered callbacks objects
     *
     * @memberOf ModalInstance
     * @private
     * @return {Array<ModalInstanceCallbackSet>} array of registered sets of callbacks
     */
    function __getCallbacks() {
        return _callbacks;
    }

    /**
     * Add {@link ModalInstanceCallbackSet} to be registered for corresponding {@link ModalInstance}.
     *
     * @memberOf ModalInstance
     * @public
     * @param {ModalInstanceCallbackSet} callbacks
     */
    function addCallbacks(callbacks) {
        if (isArray(callbacks) && !isEmpty(callbacks)) {
            callbacks.forEach((callbackObject) => {
                _pushCallbacks(callbackObject);
            });
        } else {
            _pushCallbacks(callbacks);
        }
    }

    /**
     * Remove an object of registered callbacks from this modalInstance
     *
     * @memberOf ModalInstance
     * @public
     * @param {ModalInstanceCallbackSet} callbacks
     * @throws {TypeError} when argument is an empty object or not object-like
     */
    function removeCallbacks(callbacks) {
        if (!isObjectLike(callbacks) || isEmpty(callbacks)) {
            throw new TypeError(
                `ModalInstance#removeCallbacks: argument "callbacks" should be a non-empty 'Object' (${typeof callbacks})`
            );
        }

        _callbacks = _callbacks.filter((clb) => {
            return clb !== callbacks;
        });
    }

    /**
     * Remove all registered callbacks from this modalInstance
     *
     * @public
     */
    function resetCallbacks() {
        _callbacks = [];
    }

    /**
     * Takes the on close callback from a given modal and clones it in current modal
     * @param {ModalInstance} originModal
     */
    function addOnCloseCallbackFromAnotherModal(originModal) {
        const originCallbacks = originModal.__getCallbacks();
        const onCloseCallbacksToAdd = originCallbacks
            .map((callbacks) => {
                if (isFunction(callbacks.onClose)) {
                    return { onClose: callbacks.onClose };
                }
                return null;
            })
            .filter((callback) => !!callback); // Filters out falsy values

        addCallbacks(onCloseCallbacksToAdd);
    }

    /**
     * Private method allowing to see the content of the private variables.
     * TO BE USED IN TESTS AND DEBUGGING ONLY !!!
     *
     * @private
     * @return {Object} an object containing private variable
     */
    function __debug() {
        return {
            _callbacks: [].concat(_callbacks),
            _opened,
            _props: peekAtProps(),
        };
    }

    return {
        __debug,
        __getCallbacks,
        addCallbacks,
        addOnCloseCallbackFromAnotherModal,
        close,
        isOpen,
        open,
        removeCallbacks,
        resetCallbacks,
        peekAtProps,
        updateProps,
    };
}

export default ModalInstance;
